/**
 * Combinatie-guard voor bewerkingen.
 *
 * Sommige combinaties van bewerkingen hebben een prijsregel die wij (nog) niet
 * zeker weten — bijvoorbeeld slijpwerk/facet op een niet-rechtlijnig model (de
 * +100%-slijpwerktoeslag uit de PDF is dubbelzinnig). In plaats van een mogelijk
 * FOUTE prijs te tonen, markeren we zo'n combinatie als "op aanvraag", zodat de
 * klant geen verkeerde prijs krijgt en wij de regel rustig kunnen navragen en
 * later bevestigen. Bevestigen = de bijbehorende toeslag instellen (dan vervalt
 * de hold vanzelf en wordt de combinatie normaal geprijsd).
 *
 * Puur en dependency-vrij: zowel de client (live indicatie) als de server
 * (orderautoriteit) draaien deze check, zodat een onzekere combinatie nergens
 * stiekem een prijs krijgt.
 */
import type { ConfigInput } from "../types/config";
import { type PriceContext } from "./engine";
import { boringCost, hoekCost, uitsparingCost } from "./processings";
import { isModelRoundedCorner, RONDE_HOEK_MODEL_MM } from "./surcharges";
import { facetRondeHoekCents } from "./grinding";

export interface UncertainCombination {
  /** Korte, klant-leesbare reden waarom dit op aanvraag is. */
  reason: string;
}

/**
 * Geeft een reden terug als de configuratie een (nog) niet-bevestigde combinatie
 * van bewerkingen bevat → toon "prijs op aanvraag" i.p.v. een prijs. `null` =
 * gewoon prijzen.
 */
export function uncertainCombination(
  input: ConfigInput,
  ctx: Pick<PriceContext, "variant" | "shape" | "edgeFinish" | "edgeShapeSurchargePct">,
): UncertainCombination | null {
  // 1) Bewerking waarvan we de prijs niet kunnen ophalen (geen staffel voor dit glas)
  //    → op aanvraag i.p.v. een fout, zodat de klant de prijs kan opvragen.
  for (const p of input.processings ?? []) {
    // Een afronding > 250 mm heeft bewust géén stukprijs: die telt als model en wordt in de
    // engine als paneeltoeslag (+80%) gerekend. Niet als "onprijsbaar" behandelen dus.
    if (isModelRoundedCorner(p)) continue;
    const priceable =
      p.kind === "boring" ? boringCost(ctx.variant, p.sizeMm) != null
      : p.kind === "uitsparing" ? uitsparingCost(ctx.variant, p.sizeMm, p.finished) != null
      : hoekCost(ctx.variant, p.sizeMm, p.hoekType) != null;
    if (!priceable) {
      const label =
        p.kind === "boring" ? `Boring Ø${p.sizeMm} mm`
        : p.kind === "uitsparing" ? `Uitsparing ${p.sizeMm} mm`
        : `Hoekafwerking ${p.sizeMm} mm`;
      return { reason: `${label} voor dit glas` };
    }
  }

  // 2) Afronding met doorlopend facet: het facet-werkblad geeft een prijs per hoek voor 4–6, 8–12
  //    en 15 mm, maar niet voor 19 mm ("geen prijs van"). Die combinatie krijgt dus geen bedrag —
  //    liever op aanvraag dan de gewone hoekstaffel, die een facet-hoek te goedkoop maakt.
  if (ctx.edgeFinish?.facetMm != null) {
    const rondeHoek = (input.processings ?? []).some(
      (p) => p.kind === "hoek" && (p.hoekType ?? "rond") === "rond" && p.sizeMm <= RONDE_HOEK_MODEL_MM,
    );
    if (rondeHoek && facetRondeHoekCents(ctx.variant.thicknessMm) == null) {
      return { reason: `Afgeronde hoek met doorlopend facet bij ${ctx.variant.thicknessMm} mm glas` };
    }
  }

  // 3) Slijpwerk op een model hoefde vroeger een offerte omdat de vormtoeslag onbekend was.
  //    De werkbladen leggen die nu vast (×2 bij enkel, ×1,5 bij gelaagd), dus een cirkel met
  //    PP-slijpwerk rekenen we gewoon door. Ook de afronding > 250 mm heeft nu een regel
  //    (modeltoeslag +80%, prijslijst pag. 5) en hoeft dus niet meer op aanvraag.
  return null;
}
